/**
 * Cookie Policy Page
 * GDPR compliant cookie policy information
 */

import React from 'react';
import Header from '@/components/Header';
import { Footer2 } from '@/components/ui/shadcnblocks-com-footer2';
import PageTransition from '@/components/PageTransition';
import { Cookie, Shield, BarChart3, Megaphone, Wrench, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';
import { SEO } from '@/components/SEO';

const CookiePolicy: React.FC = () => {
  const { openPreferences } = useCookieConsentContext();

  return (
    <PageTransition>
      <SEO
        title="מדיניות עוגיות (Cookies)"
        description="מדיניות העוגיות של אליס תכשיטים - מידע על סוגי העוגיות שאנו משתמשים בהן, אופן ניהול ההעדפות ושמירה על הפרטיות שלך"
        canonical="https://alice.jewelry/cookie-policy"
        breadcrumbs={[
          { name: 'דף הבית', url: '/' },
          { name: 'מדיניות עוגיות', url: '/cookie-policy' },
        ]}
      />
      <Header />
      <main className="min-h-screen bg-background py-12 md:py-20" dir="rtl">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-6">
              <Cookie className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              מדיניות עוגיות (Cookies)
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              עמוד זה מסביר כיצד אנו משתמשים בעוגיות ובטכנולוגיות דומות באתר שלנו,
              בהתאם לתקנות ה-GDPR והדירקטיבה האירופית לפרטיות אלקטרונית (ePrivacy).
            </p>
          </div>

          {/* Manage Preferences Button */}
          <div className="text-center mb-12">
            <Button onClick={openPreferences} className="bg-primary hover:bg-primary/90">
              <Shield className="h-4 w-4 ml-2" />
              נהל את העדפות העוגיות שלי
            </Button>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {/* What are Cookies */}
            <section className="prose max-w-none">
              <h2 className="text-2xl font-semibold text-foreground mb-4">מהן עוגיות?</h2>
              <p className="text-muted-foreground leading-relaxed">
                עוגיות (Cookies) הן קבצי טקסט קטנים המאוחסנים על המכשיר שלך (מחשב, טלפון או טאבלט)
                כאשר אתה מבקר באתר אינטרנט. עוגיות מאפשרות לאתר "לזכור" את הפעולות וההעדפות שלך
                לאורך זמן, כך שלא תצטרך להזין אותן מחדש בכל פעם שאתה חוזר לאתר או עובר בין דפים.
              </p>
            </section>

            {/* Cookie Categories */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-6">סוגי העוגיות שאנו משתמשים בהן</h2>
              
              <div className="grid gap-6">
                {/* Necessary Cookies */}
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-full shrink-0">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">עוגיות הכרחיות</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        עוגיות אלו חיוניות לתפקוד הבסיסי של האתר. הן מאפשרות פעולות כמו ניווט באתר,
                        גישה לאזורים מאובטחים, שמירת פריטים בעגלת הקניות והתחברות לחשבון.
                        ללא עוגיות אלו, האתר לא יפעל כראוי.
                      </p>
                      <span className="inline-block text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                        פעילות תמיד • לא ניתנות לביטול
                      </span>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-full shrink-0">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">עוגיות סטטיסטיקה ואנליטיקה</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        עוגיות אלו עוזרות לנו להבין כיצד מבקרים משתמשים באתר על ידי איסוף מידע סטטיסטי אנונימי.
                        המידע כולל את מספר המבקרים, הדפים הפופולריים ביותר ואת דרכי הניווט באתר.
                        אנו משתמשים בכלים כמו Google Analytics לצורך זה.
                      </p>
                      <div className="text-xs text-muted-foreground">
                        <strong>ספקים:</strong> Google Analytics
                      </div>
                    </div>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-full shrink-0">
                      <Megaphone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">עוגיות שיווק ופרסום</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        עוגיות אלו משמשות להצגת פרסומות רלוונטיות עבורך ולמעקב אחר יעילות קמפיינים שיווקיים.
                        הן עוזרות לנו להציג לך מוצרים ושירותים שעשויים לעניין אותך, גם באתרים אחרים.
                      </p>
                      <div className="text-xs text-muted-foreground">
                        <strong>ספקים:</strong> Meta (Facebook Pixel), Google Ads
                      </div>
                    </div>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-full shrink-0">
                      <Wrench className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">עוגיות פונקציונליות</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        עוגיות אלו מאפשרות פונקציות מתקדמות ומותאמות אישית כמו שמירת העדפות שפה,
                        צפייה בסרטוני וידאו, שילוב עם רשתות חברתיות ותכונות אינטראקטיביות נוספות.
                      </p>
                      <div className="text-xs text-muted-foreground">
                        <strong>ספקים:</strong> YouTube, Facebook, Instagram
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* How to Manage Cookies */}
            <section className="prose max-w-none">
              <h2 className="text-2xl font-semibold text-foreground mb-4">כיצד לנהל את העוגיות שלך?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                יש לך שליטה מלאה על העוגיות שמאוחסנות במכשיר שלך:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>
                  <strong>דרך האתר שלנו:</strong> לחץ על כפתור "נהל העדפות" בראש עמוד זה או בקישור "ניהול עוגיות" בתחתית כל עמוד באתר.
                </li>
                <li>
                  <strong>דרך הדפדפן שלך:</strong> רוב הדפדפנים מאפשרים לך לצפות, למחוק ולחסום עוגיות מאתרים ספציפיים.
                  פנה להגדרות הדפדפן שלך למידע נוסף.
                </li>
              </ul>
            </section>

            {/* Third Party Providers */}
            <section className="prose max-w-none">
              <h2 className="text-2xl font-semibold text-foreground mb-4">ספקי צד ג'</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                אנו עובדים עם ספקי צד ג' מהימנים לצורך אנליטיקה ושיווק:
              </p>
              <div className="grid gap-4">
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  Google - מדיניות פרטיות
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.facebook.com/privacy/policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  Meta (Facebook) - מדיניות פרטיות
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </section>

            {/* Updates */}
            <section className="prose max-w-none">
              <h2 className="text-2xl font-semibold text-foreground mb-4">עדכונים למדיניות</h2>
              <p className="text-muted-foreground leading-relaxed">
                אנו עשויים לעדכן מדיניות זו מעת לעת. כל שינוי מהותי יפורסם בעמוד זה
                עם תאריך העדכון האחרון. אנו ממליצים לעיין במדיניות זו באופן תקופתי.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                עדכון אחרון: דצמבר 2025
              </p>
            </section>

            {/* Contact */}
            <section className="prose max-w-none border-t border-border pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">יצירת קשר</h2>
              <p className="text-muted-foreground leading-relaxed">
                אם יש לך שאלות לגבי השימוש שלנו בעוגיות, אנא צור קשר:
              </p>
              <ul className="text-muted-foreground space-y-1 mt-4">
                <li>דוא"ל: <a href="mailto:info@alicejewelry.net" className="text-primary hover:underline">info@alicejewelry.net</a></li>
                <li>טלפון: <a href="tel:079-5575576" className="text-primary hover:underline">079-5575576</a></li>
              </ul>
            </section>
          </div>
        </div>
      </main>
      <Footer2 />
    </PageTransition>
  );
};

export default CookiePolicy;
